/* eslint-disable no-nested-ternary */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */
/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import Spinner from '../../../../shared/_TBComponents/Spinner';
import {
  fetchSCDashData,
  setDateType,
} from '../../ducks/sportCoachDashboardActions';
import { dashboardTracker } from '../../../../shared/utils/amplitudeHelper';

const ChartContainer = styled('div')`
  filter: ${(props) => (props.isBlurred ? 'blur(1.2px)' : null)};
  height: ${(props) => (props.isLoading ? '355px' : '90%')};
  width: 100%;

  .custom-tooltip {
    background-color: #FFFFFF;
    border: solid 1px #EEEEEE;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    font-family: 'Nunito Sans';
    font-size: 13px;
    font-weight: normal;
    justify-content: flex-start;
    max-width: 250px;
    padding: 8px 16px;
  }
`;

const DailyOption = styled('button')`
  border: none;
  color: ${(props) => (!props.isActive ? '#9E9E9E' : null)};
  font-family: 'Nunito Sans';
  font-size: 16px;
`;

const DailySelector = styled('div')`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
`;

const DailySpacer = styled('span')`
  color: #9E9E9E;
  cursor: default;
  font-family: 'Nunito Sans';
  font-size: 16px;
  margin: 0px 5px;
`;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className='custom-tooltip'>
        <p style={{ fontWeight: 'bold' }}>{label}</p>
        {payload.map((dataPoint) => (
          <p key={dataPoint.name} style={{ color: dataPoint.formatter || dataPoint.color }}>
            {`${dataPoint.name}: `}
            <span style={{ color: '#444444' }}>{dataPoint.value}</span>
          </p>
        ))}
      </div>
    );
  }

  return null;
};

const SCOverviewChart = () => {
  const {
    chartData,
    dateType,
    endDateData,
    exerciseFiltersPerformance,
    exercises,
    group,
    maxType,
    selectedAthletePerformance,
    startDateData,
    timingGateExercises,
    weightType,
  } = useSelector((state) => state.sportCoachDashboard.data);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const { isReadOnlyView, isSCDashDataLoading } = useSelector((state) => state.sportCoachDashboard.ui);

  const dispatch = useDispatch();

  const hasData = chartData.length > 0;
  const data = hasData ? [...chartData] : null;

  const exerIds = useMemo(
    () => (exercises.length > 0 ? exercises.map((item) => item.id) : []),
    [exercises],
  );

  const timingGateExerciseDetails = timingGateExercises.length > 0 ? timingGateExercises : [];

  const weight = weightType || currentUser?.userSettings?.weight;

  const yAxisLabel = useMemo(() => {
    if (exerciseFiltersPerformance.length === 0) return 'weight';
    if (exerciseFiltersPerformance[0]?.unitAbbr === 'wgt') return 'weight';
    return exerciseFiltersPerformance[0]?.unitAbbr || 'secs';
  }, [exerciseFiltersPerformance, weight]);

  const formattedData = useMemo(() => {
    if (!data) return null;
    if (dateType === 1) {
      return data.map((obj) => ({
        ...obj,
        date: moment(obj.date, 'MM/YYYY').format('MMM YY'),
      }));
    }
    return data.map((obj) => ({
      ...obj,
      date: moment(obj.date).format('MMM DD'),
    }));
  }, [data, dateType]);

  const defaultData = useMemo(() => {
    const result = [];
    const range = moment(endDateData).diff(moment(startDateData), 'months');
    for (let i = range, n = 0; i >= n; i--) {
      const day = {
        date: i !== 0
          ? moment(endDateData).subtract(i, 'months').format('MMM YY')
          : moment(endDateData).format('MMM YY'),
        exerData: 0,
      };
      result.push(day);
    }
    return result;
  }, [endDateData, startDateData]);

  const getRoundedTickIncrement = (roughStep) => {
    const exponent = Math.floor(Math.log10(roughStep));
    const magnitude = 10 ** exponent;
    const fraction = roughStep / magnitude;

    let niceFraction;
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;

    return niceFraction * magnitude;
  };

  const roundDownToStep = (value, step) => Math.floor(value / step) * step;

  const roundUpToStep = (value, step) => Math.ceil(value / step) * step;

  const { yMin, yMax, step } = useMemo(() => {
    if (!formattedData) return { yMin: 0, yMax: 350, step: 50 };

    const dataKeys = exerciseFiltersPerformance.map((f) => f.displayName ?? f.name);
    const allValues = formattedData.flatMap((d) => dataKeys.map((key) => d[key]).filter((v) => v != null));

    if (allValues.length === 0) return { yMin: 0, yMax: 350, step: 50 };

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = max - min;
    const padding = Math.max(range * 0.1, 0.05);
    const roughStep = range > 0 ? range / 5 : padding / 5;
    const roundedStep = getRoundedTickIncrement(roughStep);

    const roundedMin = Math.max(0, roundDownToStep(min - padding, roundedStep));
    const roundedMax = roundUpToStep(max + padding, roundedStep);

    return {
      yMin: Number(roundedMin.toFixed(10)),
      yMax: Number(roundedMax.toFixed(10)),
      step: Number(roundedStep.toFixed(10)),
    };
  }, [formattedData, exerciseFiltersPerformance]);

  const tickDecimals = Math.max(0, -Math.floor(Math.log10(step)));

  const yAxisTicks = useMemo(() => {
    if (yMin == null || yMax == null) return [];

    const ticks = [];
    for (let i = yMin; i <= yMax; i += step) {
      const roundedTick = Number(i.toFixed(10));
      ticks.push(roundedTick);
    }
    return ticks;
  }, [yMin, yMax, step, exerciseFiltersPerformance]);

  // detect max string length of the y-axis ticks to ensure
  // ticks and y-axis label don't overlap
  const yAxisWidth = useMemo(() => {
    if (yAxisTicks.length === 0) return 70;
    const longestTickLength = Math.max(...yAxisTicks.map((t) => t.toFixed(tickDecimals).length));
    return Math.max(70, longestTickLength * 9 + 20);
  }, [yAxisTicks, tickDecimals]);

  const colors = ['#1787FB', '#0DCC8A', '#FFCB47'];

  const filterAthleteData = (chrtData, userId, exers, timingGateExers) => {
    if (!chrtData || !userId) return null;
    return chrtData.map((dateObj) => {
      const { athleteData, ...returnObj } = dateObj;
      const matchingAthlete = athleteData.find(
        (athleteWithMaxData) => athleteWithMaxData.userId === userId,
      );
      if (matchingAthlete) {
        if (exers.length > 0) {
          exers.forEach((exercise) => {
            if (matchingAthlete.testResults[exercise.name]) {
              returnObj[`${matchingAthlete.fullName}_${exercise.name}`] = matchingAthlete.testResults[exercise.name];
            }
          });
        }
        if (timingGateExers.length > 0) {
          timingGateExers.forEach((exercise) => {
            if (matchingAthlete.testResults[exercise.displayName]) {
              returnObj[`${matchingAthlete.fullName}_${exercise.displayName}`] = matchingAthlete.testResults[exercise.displayName];
            }
          });
        }
      }
      return returnObj;
    });
  };

  const handleDateTypeClick = (type) => {
    if (isReadOnlyView) return;
    // Amplitude tracking
    dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Daily/Monthly Picker', `${type === 1 ? 'Monthly' : 'Daily'} data range selected`);
    const submissionValues = {
      startDate: startDateData,
      endDate: endDateData,
      groupId: group?.id,
      exerciseIds: exerIds,
      dateType: type,
      maxType,
      timingGateExercises: timingGateExerciseDetails,
    };
    batch(() => {
      dispatch(setDateType(type));
      dispatch(
        fetchSCDashData(
          currentUser.accountCode,
          submissionValues,
        ),
      );
    });
  };

  const handleDailyClick = () => {
    handleDateTypeClick(2);
  };

  const handleMonthlyClick = () => {
    handleDateTypeClick(1);
  };

  const renderDateSelector = () => (
    <DailySelector>
      <DailyOption
        disabled={!hasData || dateType === 2 || isReadOnlyView}
        isActive={dateType === 2}
        onClick={handleDailyClick}
      >
        Daily
      </DailyOption>
      <DailySpacer> | </DailySpacer>
      <DailyOption
        disabled={!hasData || dateType === 1 || isReadOnlyView}
        isActive={dateType === 1}
        onClick={handleMonthlyClick}
      >
        Monthly
      </DailyOption>
    </DailySelector>
  );

  const renderYAxis = (domain) => (
    <YAxis
      label={{
        angle: -90,
        fill: '#666666',
        position: 'insideLeft',
        value: yAxisLabel,
      }}
      tickFormatter={(value) => value.toFixed(tickDecimals)}
      width={yAxisWidth}
      {...(domain && { domain })}
      ticks={yAxisTicks}
    />
  );

  const renderGroupLines = () => exerciseFiltersPerformance.map((filter, i) => (
    <Line
      connectNulls
      dataKey={filter.displayName ?? filter.name}
      key={`${filter.displayName ?? filter.name}_${i}`}
      name={filter.displayName ?? filter.name}
      stroke={colors[i]}
      type='linear'
    />
  ));

  const renderAthleteLines = () => {
    if (!selectedAthletePerformance) return null;
    return exerciseFiltersPerformance.map((filter, i) => (
      <Line
        connectNulls
        dataKey={`${selectedAthletePerformance.fullName}_${filter.displayName ?? filter.name}`}
        key={`${selectedAthletePerformance.fullName}_${filter.displayName ?? filter.name}_${i}`}
        legendType='plainline'
        name={`${selectedAthletePerformance.fullName} (${filter.displayName ?? filter.name})`}
        stroke={colors[i]}
        strokeDasharray='5 5'
        type='monotone'
      />
    ));
  };

  const renderChartWithData = () => {
    if (!hasData) {
      return (
        <ResponsiveContainer height='90%' id='printableDiv' minHeight={380} width='100%'>
          <LineChart data={defaultData}>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis angle={315} dataKey='date' height={50} textAnchor='end' />
            {renderYAxis([0, 350])}
            <Line
              dataKey='exerData'
              dot={false}
              stroke='#444444'
              type='linear'
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (selectedAthletePerformance) {
      return (
        <ResponsiveContainer height='90%' id='printableDiv' minHeight={380} width='100%'>
          <LineChart data={filterAthleteData(formattedData, selectedAthletePerformance?.userId, exercises, timingGateExerciseDetails)}>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis angle={315} dataKey='date' height={50} textAnchor='end' />
            {renderYAxis([yMin, yMax])}
            {renderGroupLines()}
            {renderAthleteLines()}
            <Tooltip content={<CustomTooltip />} />
            <Legend height={exerciseFiltersPerformance.length > 1 ? 60 : 30} verticalAlign='top' />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer height='90%' id='printableDiv' minHeight={380} width='100%'>
        <LineChart data={formattedData}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis angle={315} dataKey='date' height={50} textAnchor='end' />
          {renderYAxis([yMin, yMax])}
          {renderGroupLines()}
          <Tooltip content={<CustomTooltip />} />
          <Legend height={30} verticalAlign='top' />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if (isSCDashDataLoading) {
    return (
      <ChartContainer isLoading={isSCDashDataLoading}>
        <Spinner large />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer isBlurred={!hasData}>
      {renderDateSelector()}
      {renderChartWithData()}
    </ChartContainer>
  );
};

export default SCOverviewChart;
